import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  UsersIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Link } from 'react-router';
import adminService from '../../services/admin.service';
import { AdminOverview as AdminOverviewType, AdminAuditLogItem } from '../../types/admin.types';

const TIER_COLORS: Record<string, string> = {
  free: '#9CA3AF',
  starter: '#6366F1',
  growth: '#8B5CF6',
};

const TIER_LABELS: Record<string, string> = {
  free: 'Free',
  starter: 'Starter',
  growth: 'Growth',
};

const ACTION_LABELS: Record<string, string> = {
  create_user: 'Created user',
  update_user: 'Updated user',
  send_password_reset: 'Sent password reset',
  sftp_retry: 'Retried SFTP provisioning',
  sftp_reset_password: 'Reset SFTP password',
  retry_analysis: 'Retried analysis',
  retry_failed_analyses: 'Batch retried failed analyses',
  unstick_processing: 'Unstuck processing analyses',
  change_tier: 'Changed subscription tier',
  update_ingestion_source: 'Updated ingestion source',
};

const AdminOverview: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<AdminOverviewType | null>(null);
  const [recentActivity, setRecentActivity] = useState<AdminAuditLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [overview, auditLog] = await Promise.allSettled([
          adminService.getOverview(),
          adminService.getAuditLog({ limit: 15 }),
        ]);
        if (overview.status === 'fulfilled') setData(overview.value);
        else setError('Failed to load overview data');
        if (auditLog.status === 'fulfilled') setRecentActivity(auditLog.value.items);
      } catch (err) {
        setError('Failed to load overview data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-white rounded-xl shadow" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <p className="text-sm text-red-700">{error || 'No data available'}</p>
      </div>
    );
  }

  const kpiCards = [
    {
      name: 'Monthly Recurring Revenue',
      value: `$${parseFloat(data.mrr).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      icon: CurrencyDollarIcon,
      color: 'text-green-600',
      bg: 'bg-green-50',
      href: null as string | null,
    },
    {
      name: 'Active Users (30d)',
      value: data.active_users_30d.toLocaleString(),
      subtext: `${data.total_users.toLocaleString()} total`,
      icon: UsersIcon,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
      href: null as string | null,
    },
    {
      name: 'New Signups (30d)',
      value: data.new_signups_30d.toLocaleString(),
      icon: ArrowTrendingUpIcon,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      href: null as string | null,
    },
    {
      name: 'Error Rate (30d)',
      value: `${data.error_rate_30d}%`,
      subtext: `${data.total_analyses_30d.toLocaleString()} analyses`,
      icon: ExclamationTriangleIcon,
      color: data.error_rate_30d > 5 ? 'text-red-600' : 'text-gray-600',
      bg: data.error_rate_30d > 5 ? 'bg-red-50' : 'bg-gray-50',
      href: '/admin/analyses?status=failed',
    },
  ];

  const tierData = Object.entries(data.users_by_tier).map(([tier, count]) => ({
    name: TIER_LABELS[tier] || tier,
    value: count,
    color: TIER_COLORS[tier] || '#9CA3AF',
  }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Platform Overview</h1>
        <p className="mt-1 text-sm text-gray-500">Last 30 days performance</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {kpiCards.map((card) => {
          const Wrapper = card.href ? 'button' : 'div';
          return (
            <Wrapper
              key={card.name}
              onClick={card.href ? () => navigate(card.href!) : undefined}
              className={`overflow-hidden rounded-xl bg-white shadow text-left w-full ${
                card.href ? 'hover:shadow-md transition-shadow cursor-pointer' : ''
              }`}
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 rounded-md ${card.bg} p-3`}>
                    <card.icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="truncate text-sm font-medium text-gray-500">{card.name}</dt>
                      <dd className="flex items-baseline">
                        <p className="text-2xl font-semibold text-gray-900">{card.value}</p>
                        {card.subtext && (
                          <p className="ml-2 text-sm text-gray-500">{card.subtext}</p>
                        )}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </Wrapper>
          );
        })}
      </div>

      {/* Secondary metrics row */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Users by Tier */}
        <div className="rounded-xl bg-white shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Users by Tier</h3>
          <div className="flex items-center gap-8">
            <div className="w-48 h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={tierData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    dataKey="value"
                  >
                    {tierData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-3">
              {tierData.map((tier) => (
                <div key={tier.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: tier.color }}
                    />
                    <span className="text-sm text-gray-700">{tier.name}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    {tier.value.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Platform Usage */}
        <div className="rounded-xl bg-white shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Platform Usage (30d)</h3>
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 rounded-md bg-purple-50 p-3">
                <ChartBarIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Total Analyses</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {data.total_analyses_30d.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 rounded-md bg-cyan-50 p-3">
                <ClockIcon className="h-6 w-6 text-cyan-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Total Minutes Processed</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {parseFloat(data.total_minutes_30d).toLocaleString('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <div className="rounded-xl bg-white shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {recentActivity.map((entry) => (
              <div key={entry.id} className="flex items-start gap-3 text-sm">
                <span className="text-gray-500 whitespace-nowrap min-w-[140px]">
                  {new Date(entry.created_at).toLocaleString(undefined, {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}
                </span>
                <span className="text-gray-700 font-medium">{entry.admin_email}</span>
                <span className="text-gray-600">
                  {ACTION_LABELS[entry.action] || entry.action}
                </span>
                {entry.target_user_email && (
                  <Link
                    to={`/admin/users/${entry.target_user_id}`}
                    className="text-indigo-600 hover:text-indigo-500"
                  >
                    {entry.target_user_email}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOverview;
